import { BadgeDollarSignIcon, ChevronRightIcon, CopyCheckIcon, HeartHandshakeIcon } from 'lucide-react'
import { motion } from 'motion/react'
import React from 'react'

import { Button, TextLink } from 'slab'
import { useWindowLocation } from 'utils/windowLocation'

import WelcomeWrapper from '../components/WelcomeWrapper'

const timings = {
  delays: {
    titleStart: 0.25,
    titleMoveUp: 1.75,
    allowance: 2.5,
    spending: 3.5,
    benefits: 4.5,
    button: 5.5
  },
  durations: {
    titleFadeIn: 2.4,
    titleMoveUp: 0.8,
    textItems: 0.8,
    button: 0.8
  }
}

const gradientTextStyle: React.CSSProperties = {
  opacity: 0.7,
  background: 'linear-gradient(248deg, #FF5900 6.63%, #B700FF 38.31%, #4000FF 71.67%, #00BFFF 99.93%)',
  backgroundClip: 'text',
  color: 'transparent'
}

const variants = {
  title: {
    hidden: {
      opacity: 0,
      y: 100
    },
    visible: {
      opacity: 1,
      y: 0
    }
  },
  textItem: {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  },
  button: {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1
    }
  }
}

interface AnimatedTextItemProps {
  icon: React.ComponentType<{ className?: string }>
  text: string
  delay: number
  duration: number
}

const AnimatedTextItem: React.FC<AnimatedTextItemProps> = ({
  icon: Icon,
  text,
  delay,
  duration
}) => {
  return (
    <motion.div
      className='pt-6'
      variants={variants.textItem}
      initial='hidden'
      animate='visible'
      transition={{
        delay,
        duration,
        ease: 'easeInOut'
      }}
    >
      <div className='flex items-center gap-3'>
        <div className='relative'>
          <svg xmlns='http://www.w3.org/2000/svg' width='36' height='36' viewBox='0 0 36 36' className='text-thatch-200'>
            <circle cx='18' cy='18' r='18' fill='currentColor' />
          </svg>
          <Icon className='absolute inset-0 m-auto w-5 h-5 text-purple-400' />
        </div>
        <div className='text-thatch-gray-secondary font-inter text-sm'>{text}</div>
      </div>
    </motion.div>
  )
}

interface GetStartedPageProps {
  path: string
  logoImagePath: string
  logoAllCapImagePath: string
  employeeName: string
  wrongInvitePath: string
}

export const GetStartedPage: React.FC<GetStartedPageProps> = (
  {
    path,
    logoImagePath,
    logoAllCapImagePath,
    employeeName,
    wrongInvitePath
  }
) => {
  const { assign } = useWindowLocation()

  const handleGetStarted = () => {
    assign(path)
  }

  return (
    <WelcomeWrapper
      logoImagePath={logoImagePath}
      logoAllCapImagePath={logoAllCapImagePath}
    >
      <div className='flex flex-col'>
        <motion.h1
          className='heading-lg mb-4'
          variants={variants.title}
          initial='hidden'
          animate='visible'
          transition={{
            delay: timings.delays.titleStart,
            opacity: { duration: timings.durations.titleFadeIn, ease: 'easeInOut' },
            y: { duration: timings.durations.titleMoveUp, delay: timings.delays.titleMoveUp, ease: 'easeInOut' }
          }}
        >
          {employeeName ? `${employeeName}, welcome` : 'Welcome'} to a <span style={gradientTextStyle}>new way</span> to do healthcare
        </motion.h1>

        <AnimatedTextItem
          icon={BadgeDollarSignIcon}
          text='Your company offers you an allowance'
          delay={timings.delays.allowance}
          duration={timings.durations.textItems}
        />

        <AnimatedTextItem
          icon={HeartHandshakeIcon}
          text='We help you spend it'
          delay={timings.delays.spending}
          duration={timings.durations.textItems}
        />

        <AnimatedTextItem
          icon={CopyCheckIcon}
          text='Your benefits are up to you'
          delay={timings.delays.benefits}
          duration={timings.durations.textItems}
        />

        <motion.div
          className='w-full pt-[3.75rem] gap-3 flex flex-col'
          variants={variants.button}
          initial='hidden'
          animate='visible'
          transition={{
            delay: timings.delays.button,
            duration: timings.durations.button,
            ease: 'easeInOut'
          }}
        >
          <Button
            onClick={handleGetStarted}
            variant='primary'
            size='large'
            width='full'
            rightIcon={ChevronRightIcon}
          >
            Get started
          </Button>
          {employeeName && (
            <span className='text-gray-500 text-center' data-testid='wrong_invite_text'>
              Not {employeeName}? Click{' '}
              <TextLink href={wrongInvitePath}>
                here
              </TextLink>
            </span>
          )}
        </motion.div>
      </div>
    </WelcomeWrapper>
  )
}

export default GetStartedPage
